import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  Typography,
  Paper,
  Stack,
  Switch,
  Skeleton,
  FormGroup,
  FormControlLabel,
  Fade,
} from "@mui/material";
import __ from "src/utils/Translations";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import DashboardQueries from "src/Pages/Company/Dashboard/services/api/queryOptions";
import { updateDashboardModuleConfiguration } from "src/Pages/Company/Dashboard/services/api/api";
import { dynamicNotification } from "src/utils/Notifications";
import { dashboardModuleSettingsSchema } from "./DashboardModuleSettings.schema";

type FormValues = z.infer<typeof dashboardModuleSettingsSchema>;

const DashboardModuleSettings = () => {
  const companyId = useCompanyId();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, isFetching } = useQuery(
    DashboardQueries.getDashboardModuleConfiguration.byCompanyId(
      true,
      companyId
    )
  );

  const { mutate, isPending } = useMutation({
    mutationFn: (payload: { dashboardVisible: boolean }) =>
      updateDashboardModuleConfiguration({ companyId, payload }),
    onError: () => {
      dynamicNotification(__("Nie udało się zapisać ustawień."), "error");
    },
    onSuccess: () => {
      dynamicNotification(__("Pomyślnie zapisano ustawienia."), "success");
      queryClient.invalidateQueries({
        queryKey: DashboardQueries.getDashboardModuleConfiguration.all(),
      });
    },
  });

  const { control, handleSubmit } = useForm<FormValues>({
    resolver: zodResolver(dashboardModuleSettingsSchema),
    defaultValues: { dashboardVisibility: false },
    values: data ? { dashboardVisibility: data.dashboardVisible } : undefined,
  });

  const onSubmit = (formData: FormValues) => {
    mutate({ dashboardVisible: formData.dashboardVisibility });
  };

  return (
    <Fade in={!isLoading}>
      <Paper sx={{ padding: "2rem" }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap="1.5rem">
            <Stack gap="0.75rem">
              <Typography variant="h5">
                {__("Widoczność dashboardu")}
              </Typography>
              <Typography>
                {__(
                  "Po włączeniu dashboardu użytkownicy będą widzieli moduły, których widoczność jest im przypisana."
                )}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center">
              {isLoading ? (
                <Skeleton variant="rectangular" height="46px" width="196px" />
              ) : (
                <FormGroup>
                  <Controller
                    name="dashboardVisibility"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value}
                            onChange={(e) => {
                              field.onChange(e);
                              handleSubmit(onSubmit)();
                            }}
                            disabled={isError || isFetching || isPending}
                          />
                        }
                        label={__(
                          field.value
                            ? "Dashboard widoczny"
                            : "Dashboard ukryty"
                        )}
                      />
                    )}
                  />
                </FormGroup>
              )}
            </Stack>
          </Stack>
        </form>
      </Paper>
    </Fade>
  );
};

export default DashboardModuleSettings;
